import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { CalendarDays, MapPin, PartyPopper, ArrowRight, Leaf } from "lucide-react";

const SagreStrip = () => {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/sagre/mese").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const monthLabel = t(`month_${data.month}`);
  const sagre = data.sagre || [];

  return (
    <section data-testid="sagre-strip" className="py-24 md:py-32 bg-pietra-light relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="eyebrow mb-4 flex items-center gap-2">
              <PartyPopper size={14} strokeWidth={1.8} />
              <span>{t("sagre_eyebrow")} · {monthLabel}</span>
            </div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] text-grafite mb-6">
              {t("sagre_title")}
            </h2>
            <p className="text-lg text-grafite-soft leading-relaxed">{t("sagre_intro")}</p>
          </div>
          {sagre.length > 0 && (
            <div className="text-right">
              <div className="font-serif text-6xl text-miele leading-none">{data.count}</div>
              <div className="small-caps text-grafite-soft mt-1">
                {lang === "it" ? (data.count === 1 ? "sagra attiva" : "sagre attive") : (data.count === 1 ? "active festival" : "active festivals")}
              </div>
            </div>
          )}
        </div>

        {sagre.length === 0 ? (
          <div className="p-12 bg-pietra rounded-sm border border-carrubo/15 text-center">
            <PartyPopper size={40} className="text-miele mx-auto mb-4 opacity-60" strokeWidth={1.3} />
            <p className="text-lg text-grafite-soft italic">{t("sagre_no_events")}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sagre.map((s) => (
              <article key={s.slug} data-testid={`sagra-card-${s.slug}`}
                className="group relative bg-pietra rounded-sm border border-carrubo/15 hover:border-carrubo/60 transition-all p-8 overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-miele/10 rounded-full -translate-y-16 translate-x-16" />
                <div className="relative">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-12 h-12 bg-carrubo text-pietra-light rounded-sm flex items-center justify-center">
                      <PartyPopper size={20} strokeWidth={1.4} />
                    </div>
                    {s.linked_product && (
                      <Link to={`/prodotti/${s.linked_product}`}
                        className="inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-widest text-carrubo hover:text-miele-dark">
                        <Leaf size={10} strokeWidth={2.4} />
                        {t("sagre_linked_product")}
                      </Link>
                    )}
                  </div>

                  <h3 className="font-serif text-2xl text-grafite mb-3 leading-tight group-hover:text-carrubo transition-colors">
                    {s[lang].title}
                  </h3>

                  <div className="space-y-2 mb-5 text-sm">
                    <div className="flex items-start gap-2 text-grafite-soft">
                      <CalendarDays size={14} className="mt-0.5 text-miele flex-shrink-0" />
                      <span>{s.days_range}</span>
                    </div>
                    <div className="flex items-start gap-2 text-grafite-soft">
                      <MapPin size={14} className="mt-0.5 text-miele flex-shrink-0" />
                      <span>{s.location}</span>
                    </div>
                  </div>

                  <p className="text-sm text-grafite-soft leading-relaxed">{s[lang].description}</p>

                  {s.unesco_site && (
                    <div className="mt-5 pt-4 border-t border-carrubo/10">
                      <Link to={`/territorio/${s.unesco_site}`}
                        data-testid={`sagra-unesco-${s.slug}`}
                        className="inline-flex items-center gap-2 text-xs text-carrubo hover:text-miele-dark group/link">
                        <span className="small-caps">Sito UNESCO</span>
                        <ArrowRight size={12} className="group-hover/link:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default SagreStrip;
